// Project pickers, the timer card and the agent session card: the design system's cards with the app's actions.
import { useEffect, useState, type ReactNode } from 'react';
import type { AgentSession, Project, TimeEntry } from '../api/types.ts';
import { isOwner, activeProjects, displayTitle, project as findProject, timerDraftProject } from '../model/data.ts';
import * as m from '../model/mutations.ts';
import type { SessionView } from '../model/snapshot.ts';
import { navigate } from '../lib/router.ts';
import { useFmt, useStore, useT } from '../store/hooks.tsx';
import { Button, Monogram, ProjectChip, StatusPill, TimerDisplay, cx } from '../ui/ds.tsx';
import { Icon } from '../ui/Icon.tsx';
import { SessionStrip } from '../ui/timeline.tsx';
import { Menu, MenuDivider, MenuItem, MenuLabel } from './common.tsx';

/** Picks the timer's project: the project chip, or "Projekt wählen". No "Ohne Projekt": a timer always runs for a project. */
export function ProjectPicker({ selectedId, onSelect, label }: { selectedId: string | null; onSelect: (id: string | null) => void; label?: string }) {
  const store = useStore();
  const t = store.t;
  const selected = findProject(store.ws, selectedId);
  return (
    <Menu label={label ?? t('Projekt für den Timer')} trigger={(props) => (
      selected
        ? <button type="button" className="chip-button" {...props} title={t('Projekt für den Timer')}><ProjectChip name={selected.name} color={selected.colorSlot} /></button>
        : <Button size="sm" icon="folder" {...props}>{t('Projekt wählen')}</Button>
    )}>
      {(menu) => (
        <>
          {activeProjects(store.ws).map((p) => (
            <MenuItem key={p.id} checked={p.id === selectedId} onSelect={() => { onSelect(p.id); menu.close(); }}>
              <span className="menu__proj"><span className="tl-dot" style={{ background: `var(--project-${p.colorSlot})` }} />{p.name}</span>
            </MenuItem>
          ))}
          {isOwner(store.ws) ? (
            <>
              <MenuDivider />
              <MenuItem icon="plus" onSelect={() => { menu.close(); store.openSheet({ kind: 'project', projectId: null, then: (id) => onSelect(id) }); }}>{t('Neues Projekt …')}</MenuItem>
            </>
          ) : null}
        </>
      )}
    </Menu>
  );
}

/** "Projekt zuordnen" for a session. Remembering adds a rule for its git remote (only sessions with a synced remote). */
export function AssignProjectMenu({ session, loaded, size = 'sm', variant = 'tinted', compact = false }: { session: AgentSession; loaded: readonly AgentSession[]; size?: 'sm' | 'md'; variant?: 'tinted' | 'plain'; compact?: boolean }) {
  const store = useStore();
  const t = store.t;
  const [remember, setRemember] = useState(true);
  const selected = compact ? findProject(store.ws, session.projectId) : undefined;
  const canRemember = store.pro && isOwner(store.ws) && !!session.gitRemote;
  // A rule for the repository is automatic assignment: Pro (spec/pro.md).
  const assign = (projectId: string) => void store.apply(m.assignSession(store.ws, session, projectId, remember && canRemember, loaded, store.clock()));
  return (
    <Menu label={t('Projekt zuordnen')} trigger={(props) => (
      <Button size={size} variant={compact ? 'plain' : variant} icon={selected ? undefined : compact ? 'plus' : 'folder'}
        className={compact ? 'session-project' : undefined} {...props}>
        {selected ? <><ProjectChip name={selected.name} color={selected.colorSlot} plain /><Icon name="chevronDown" size={12} /></> : t('Projekt zuordnen')}
      </Button>
    )}>
      {(menu) => (
        <>
          <MenuLabel>{t('Projekt zuordnen')}</MenuLabel>
          {activeProjects(store.ws).map((p) => (
            <MenuItem key={p.id} checked={p.id === session.projectId} onSelect={() => { assign(p.id); menu.close(); }}>
              <span className="menu__proj"><span className="tl-dot" style={{ background: `var(--project-${p.colorSlot})` }} />{p.name}</span>
            </MenuItem>
          ))}
          {canRemember ? (
            <>
              <MenuDivider />
              <MenuItem checked={remember} onSelect={() => setRemember(!remember)}>{t('Für dieses Repo merken')}</MenuItem>
            </>
          ) : null}
          {isOwner(store.ws) ? (
            <>
              <MenuDivider />
              <MenuItem icon="plus" onSelect={() => {
                menu.close();
                store.openSheet({ kind: 'project', projectId: null, then: (id) => assign(id) });
              }}>{t('Neues Projekt …')}</MenuItem>
            </>
          ) : null}
        </>
      )}
    </Menu>
  );
}

/** The running timer, or the one you are about to start (Today's hero card). */
export function TimerCard({ running, now, agentsCounting }: { running: TimeEntry | undefined; now: number; agentsCounting: number }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const [text, setText] = useState(running?.description ?? store.ui.draftDescription);
  useEffect(() => setText(running?.description ?? store.ui.draftDescription), [running?.id, running?.description, store.ui.draftDescription]);
  const project = running ? findProject(store.ws, running.projectId) : timerDraftProject(store.ws, store.ui.draftProjectId);
  const projectId = project?.id ?? null;
  const commitText = () => {
    if (running && text !== running.description) void store.apply(m.updateRunning(running, { description: text }, store.clock()));
    if (!running) store.setUi({ draftDescription: text });
  };
  const canStart = m.canStartTimer(project);
  const toggle = () => {
    if (store.timerSettling) return;
    const at = store.clock();
    if (running) {
      store.setUi({ draftDescription: running.description, draftProjectId: running.projectId ?? store.ui.draftProjectId });
      void store.apply(m.stopTimer(running, at));
    } else {
      if (!canStart) return;
      void store.apply(m.startTimer(store.actor(), undefined, { description: text, project }, at));
      store.setUi({ draftDescription: '' });
    }
    store.settleTimer();
  };
  return (
    <section className="tl-card tl-tcard" aria-label={t('Timer')}>
      <div className="tl-tcard__main">
        <div className="tl-tcard__status">
          {running ? <StatusPill state="running" label={t('Läuft seit %@', fmt.time(running.startAt))} /> : <StatusPill state="idle" label={t('Bereit')} />}
        </div>
        <input className="tl-tcard__input" value={text} placeholder={t('Woran arbeitest du?')} aria-label={t('Beschreibung')}
          onChange={(e) => setText(e.target.value)} onBlur={commitText}
          onKeyDown={(e) => { if (e.key === 'Enter') { commitText(); if (!running) toggle(); } }} />
        <div className="tl-tcard__meta">
          <ProjectPicker selectedId={projectId} onSelect={(id) => {
            if (running) void store.apply(m.updateRunning(running, { project: findProject(store.ws, id) ?? null }, store.clock()));
            else store.setUi({ draftProjectId: id });
          }} />
          {project?.billable && project.rateCents !== null
            ? <span className="tl-caption" style={{ color: 'var(--ink-2)' }}>{`${t('Abrechenbar')} · ${t('%@/h', fmt.money(project.rateCents, project.currency))}`}</span>
            : project && !project.billable ? <span className="tl-caption" style={{ color: 'var(--ink-2)' }}>{t('Intern · nicht abrechenbar')}</span> : null}
          {agentsCounting > 0 ? <span className="tl-tcard__agents"><span className="tl-live" aria-hidden="true" />{t('%lld Agents zählen mit', agentsCounting)}</span> : null}
        </div>
        {!running && !canStart ? <span className="tl-caption" style={{ color: 'var(--ink-2)' }}>{t('Zum Starten braucht der Timer ein Projekt.')}</span> : null}
      </div>
      <div className="tl-tcard__side">
        <TimerDisplay ms={running ? now - running.startAt : 0} state={running ? 'running' : 'stopped'} size="hero" />
        {running
          ? <Button variant="primary" size="lg" icon="stop" onClick={toggle} disabled={store.timerSettling}>{t('Stoppen')}</Button>
          : <Button variant="signal" size="lg" icon="play" onClick={toggle} disabled={!canStart || store.timerSettling}>{t('Starten')}</Button>}
      </div>
    </section>
  );
}

/** The design system's AgentSessionCard with the app's project menu and actions. */
export function SessionCard({ view, now, loaded }: { view: SessionView; now: number; loaded: readonly AgentSession[] }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const r = view.record;
  const note = r.method === 'logs' ? t('über Session-Logs') : view.childCount > 0 ? t('+%lld Subagents', view.childCount) : null;
  return (
    <article className="tl-card tl-agent">
      <div className="tl-agent__head">
        <Monogram text={view.provider.mono} provider={view.provider.id} />
        <div className="tl-agent__who">
          <div className="tl-agent__prov">{`${view.provider.name} · ${t('seit %@', fmt.started(r.startedAt, now))}`}{note ? ` · ${note}` : ''}</div>
          <button type="button" className="tl-agent__title linklike" title={displayTitle(r)} onClick={() => navigate({ name: 'session', id: r.id })}>{displayTitle(r)}</button>
        </div>
        <StatusPill state={view.state} />
      </div>
      <div className="tl-agent__where">
        <AssignProjectMenu session={r} loaded={loaded} compact />
        {r.repoName ? <span className="tl-mono"><Icon name="folder" size={13} />{r.repoName}</span> : null}
        {r.gitBranch ? <span className="tl-mono"><Icon name="branch" size={13} />{r.gitBranch}</span> : null}
      </div>
      <SessionStrip segments={view.segments} />
      <div className="tl-agent__nums">
        <div className="tl-agent__num"><span className="tl-caption">{t('Gezählt')}</span><TimerDisplay ms={view.countedMs} state={view.state === 'working' ? 'running' : 'paused'} size="sm" /></div>
        <div className="tl-agent__num tl-agent__num--waiting"><span className="tl-caption">{t('Gewartet · nicht gezählt')}</span><TimerDisplay ms={view.waitingMs} state="paused" size="sm" /></div>
        <div className="tl-agent__actions">
          <Menu label={t('Mehr')} align="end" trigger={(props) => <Button variant="plain" size="sm" iconOnly icon="more" aria-label={t('Mehr')} {...props} />}>
            {(menu) => (
              <>
                <MenuItem onSelect={() => { menu.close(); navigate({ name: 'session', id: r.id }); }}>{t('Details öffnen')}</MenuItem>
                {view.project ? (
                  <>
                    <MenuDivider />
                    <MenuLabel>{t('Anderem Projekt zuordnen')}</MenuLabel>
                    {activeProjects(store.ws).filter((p) => p.id !== r.projectId).map((p: Project) => (
                      <MenuItem key={p.id} onSelect={() => { menu.close(); void store.apply(m.assignSession(store.ws, r, p.id, false, loaded, store.clock())); }}>
                        <span className="menu__proj"><span className="tl-dot" style={{ background: `var(--project-${p.colorSlot})` }} />{p.name}</span>
                      </MenuItem>
                    ))}
                  </>
                ) : null}
              </>
            )}
          </Menu>
        </div>
      </div>
    </article>
  );
}

export function RowButton({ onClick, children, className, label }: { onClick: () => void; children: ReactNode; className?: string; label?: string }) {
  return <button type="button" className={cx('row-button', className)} onClick={onClick} aria-label={label}>{children}</button>;
}
